import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Grid } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

import { Block, Header, List } from '../CommonComponents';

import { FetchPropsType } from '../CommonComponents/Fetch/FetchPropsType';
import { WatchlistType } from '../../types';
import { fetchWatchlistById, selectFetchingWatchlistById, selectWatchlistById } from '../../redux/actions/watchlists';
import FetchRedux from '../CommonComponents/Fetch/FetchRedux';
import { Container } from '../StyledComponents';
import {
    formatDateTime,
    formatActive,
    formatMatchMode,
    formatSource,
    formatWatchlistArea,
    formatRange,
    formatType
} from '../../utils/parsing';

function WatchlistDetails(props: FetchPropsType<WatchlistType>) {
    const { entity } = props;
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <Container>
            <Header
                title={`Bevakning: ${entity.name}`}
                subtitle={`Användare: ${entity.userName} på ${entity.tenantName}`}
                CustomComponent={(
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        // Where the form goes after saving (from a user's tab, see UserWatchlists)
                        onClick={() => navigate(`/watchlists/${entity.id}/edit`, { state: location.state })}
                    >
                        Redigera
                    </Button>
                )}
            />
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12}>
                    <Block
                        title="Grunduppgifter"
                        variant="outlined"
                    >
                        <List
                            data={[{
                                name: 'Typ',
                                value: formatType(entity)
                            }, {
                                name: 'Område',
                                value: formatWatchlistArea(entity)
                            }, {
                                name: 'Matchning',
                                value: formatMatchMode(entity)
                            }, {
                                name: 'Källa',
                                value: formatSource(entity)
                            }, {
                                name: 'Status',
                                value: formatActive(entity)
                            }, {
                                name: 'Avsändare',
                                value: entity.publisherTenantName,
                                url: entity.publisherTenantId ? `/tenants/${entity.publisherTenantId}` : undefined
                            }, {
                                name: 'Metadata',
                                value: 'Hämtas från företagets profil'
                            }, {
                                name: 'Direktnotiser',
                                value: entity.instantNotifications,
                                type: 'bool'
                            }, {
                                name: 'Dagliga notiser',
                                value: entity.dailyNotifications,
                                type: 'bool'
                            }, {
                                name: 'Aktiva e-postnotiser',
                                value: entity.activeEmailNotifications,
                                type: 'bool'
                            }, {
                                name: 'Totalvikt [kg]',
                                value: formatRange(entity.minGrossWeight, entity.maxGrossWeight, 'kg')
                            }, {
                                name: 'FDR-vikt [kg]',
                                value: formatRange(entity.minChargeableWeight, entity.maxChargeableWeight, 'kg')
                            }, {
                                name: 'Volym [m³]',
                                value: formatRange(entity.minVolume, entity.maxVolume, 'm³')
                            }, {
                                name: 'Flakmeter',
                                value: formatRange(entity.minLoadingMeters, entity.maxLoadingMeters, 'flm')
                            }, {
                                name: 'Pallplatser',
                                value: formatRange(entity.minPalletPlaces, entity.maxPalletPlaces, 'st')
                            }, {
                                name: 'Pallar',
                                value: formatRange(entity.minPallets, entity.maxPallets, 'st')
                            }, {
                                name: 'Endast uppdrag i grupp',
                                value: entity.groupName,
                                url: entity.groupId ? `/groups/${entity.groupId}` : undefined
                            }, {
                                name: 'Endast uppdrag med riktpris',
                                value: entity.hasListPrice,
                                type: 'bool'
                            }, {
                                name: 'Externa uppdrag',
                                value: entity.isJob && !entity.takesOffer,
                                type: 'bool'
                            }, {
                                name: 'Skapad',
                                value: !entity.createdAt
                                    ? '' : `${formatDateTime(entity.createdAt)} av ${entity.createdByName || '-'}`,
                                url: entity.createdBy ? `/users/${entity.createdBy}` : undefined
                            }, {
                                name: 'Uppdaterad',
                                value: !entity.updatedAt ? '' : `${formatDateTime(entity.updatedAt)} av ${entity.updatedByName || '-'}`,
                                url: entity.updatedBy ? `/users/${entity.updatedBy}` : undefined
                            }]}
                        />
                    </Block>
                </Grid>
            </Grid>
        </Container>
    );
}

export default function WatchlistDetailsFetched({ hasRendered }: { hasRendered?: boolean }) {
    const { id } = useParams() as unknown as { id: number };

    return (
        <FetchRedux<WatchlistType>
            fetchEntity={fetchWatchlistById(id)}
            selectEntity={selectWatchlistById(id)}
            selectIsFetching={selectFetchingWatchlistById(id)}
            Component={WatchlistDetails}
            name="bevakningen"
            initialFetch={!hasRendered}
        />
    );
}
